import React, { useState } from "react";
import { Box, Paper, Typography, Grid, Button, List, ListItem, ListItemIcon, ListItemText, Divider } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import EmailIcon from "@mui/icons-material/Email";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Swal from "sweetalert2";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import client from "../api/client";
import { useNavigate } from "react-router-dom";

export default function SelfDiscoveryCheckout() {
  const amountUsd = 37;
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: payment, 2: info
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [paymentMethod, setPaymentMethod] = useState('PAYPAL');
  const [paypalOrderId, setPaypalOrderId] = useState(null);
  const navigate = useNavigate();
  const paypalClientId = "ATnw78Oayf8yprroSiVf76tpwcYqe-TY_CbVSvJmPrVedNkhN0AJzsxWzzqMo5nFBMXa2eRSO9lCMoBy";

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e, approvedOrderId = paypalOrderId) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      await client.post('/self-discovery/create', {
        ...form,
        amount: amountUsd,
        currency: 'USD',
        paymentMethod,
        paypalOrderId: approvedOrderId,
        phone: form.phone,
      });
      Swal.fire({ title: 'Success', text: 'We will email you the booking link shortly.', icon: 'success', confirmButtonText: 'OK' })
        .then((result) => { if (result.isConfirmed) navigate('/thank-you-self-discovery'); });
    } catch (e) {
      Swal.fire({ title: 'Error', text: 'There was an issue submitting your info. Please try again.', icon: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWhish = async () => {
    setIsProcessing(true);
    try {
      await Swal.fire({
        title: 'Next Steps',
        html: `Please send <strong>$${amountUsd.toFixed(2)} USD</strong> via Whish/OMT to <strong>+12108128269</strong> (Sara Hafez).<br/><br/>After payment, send us a message either by email at <strong>hello@sarasjournal.com</strong> or on WhatsApp at <strong>+12108128269</strong> with your full name, email, phone number, and a screenshot or picture of the Whish/OMT receipt. We’ll confirm within 24 hours and send your journal, guide & tests.`,
        icon: 'info',
        confirmButtonText: 'Got it',
      });
    } catch (e) {
      Swal.fire({ title: 'Error', text: 'There was an issue processing your request. Please try again later.', icon: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Add validation for PayPal form fields
  const isPaypalFormValid = form.firstName && form.lastName && form.email && form.phone && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && /^[0-9]+$/.test(form.phone);

  if (step === 1) {
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper elevation={3} sx={{ p: { xs: 2.5, md: 3 }, bgcolor: 'background.paper', maxWidth: 680, margin: '0 auto' }}>
              <Typography variant="h4" color="text.primary" gutterBottom>
                Self-Discovery Session Checkout
              </Typography>
              <Typography variant="body1" color="text.secondary">
                A personalized wellbeing session designed to help you uncover patterns, remove blockers, and create a clear path forward.
              </Typography>
              <Typography variant="h6" color="text.primary" gutterBottom sx={{ mt: 3 }}>
                Choose Your Payment Method
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Amount:
                 ${amountUsd.toFixed(2)} USD
              </Typography>
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Box component="img" src="/assets/cyj-logo.jpeg" alt="Package" sx={{ width: 90, height: 90, objectFit: 'contain' }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1, border: paymentMethod === 'PAYPAL' ? '2px solid #1976d2' : '1px solid #ccc', borderRadius: 2, p: 2, bgcolor: paymentMethod === 'PAYPAL' ? '#e3f2fd' : '#fff', transition: 'all 0.2s' }}>
                  <Button
                    variant={paymentMethod === 'PAYPAL' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => setPaymentMethod('PAYPAL')}
                    fullWidth
                  >
                    PayPal/Credit, Debit Card
                  </Button>
                  {paymentMethod === 'PAYPAL' && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                        Pay securely with PayPal or card (no PayPal account required where available)
                      </Typography>
                      <form style={{ marginTop: 20 }} autoComplete="off">
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <input
                              name="firstName"
                              value={form.firstName}
                              onChange={handleFormChange}
                              placeholder="First Name"
                              required
                              style={{ width: '100%', padding: 12, marginBottom: 10, borderRadius: 6, border: '1.5px solid #888', fontSize: '1.05em', boxSizing: 'border-box' }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <input
                              name="lastName"
                              value={form.lastName}
                              onChange={handleFormChange}
                              placeholder="Last Name"
                              required
                              style={{ width: '100%', padding: 12, marginBottom: 10, borderRadius: 6, border: '1.5px solid #888', fontSize: '1.05em', boxSizing: 'border-box' }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <input
                              name="email"
                              type="email"
                              value={form.email}
                              onChange={handleFormChange}
                              placeholder="Email Address"
                              required
                              style={{ width: '100%', padding: 12, marginBottom: 10, borderRadius: 6, border: '1.5px solid #888', fontSize: '1.05em', boxSizing: 'border-box' }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <input
                              name="phone"
                              value={form.phone}
                              onChange={handleFormChange}
                              placeholder="Phone Number"
                              pattern="[0-9]+"
                              inputMode="numeric"
                              required
                              style={{ width: '100%', padding: 12, marginBottom: 10, borderRadius: 6, border: '1.5px solid #888', fontSize: '1.05em', boxSizing: 'border-box' }}
                            />
                          </Grid>
                        </Grid>
                      </form>
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                        <Box sx={{ width: '100%', maxWidth: 420 }}>
                          <PayPalScriptProvider options={{
                            'client-id': paypalClientId,
                            currency: 'USD',
                            components: 'buttons',
                            'enable-funding': 'card',
                            'disable-funding': 'sepa,paylater,venmo',
                          }}>
                            <PayPalButtons
                              fundingSource="paypal"
                              style={{ layout: 'vertical', shape: 'rect', width: '100%' }}
                              createOrder={async (data, actions) => {
                                const orderId = await actions.order.create({
                                  purchase_units: [
                                    { amount: { value: amountUsd.toFixed(2) }, description: 'Self-Discovery Session' }
                                  ]
                                });
                                try { console.log('PayPal createOrder -> orderId:', orderId); } catch { }
                                setPaypalOrderId(orderId);
                                return orderId;
                              }}
                              onApprove={async (data, actions) => {
                                setIsProcessing(true);
                                try {
                                  await actions.order.capture();
                                  await handleFormSubmit({ preventDefault: () => {} }, data?.orderID || paypalOrderId);
                                  setForm({ firstName: '', lastName: '', email: '', phone: '' });
                                } catch (e) {
                                  Swal.fire({ title: 'Payment Issue', text: 'We could not confirm your payment. Please try again or contact support.', icon: 'error' });
                                } finally {
                                  setIsProcessing(false);
                                }
                              }}
                              onError={(err) => {
                                const details = err?.message ? ` (${err.message})` : '';
                                Swal.fire({ title: 'PayPal Error', text: `PayPal could not complete the payment${details}. Please try again.`, icon: 'error' });
                              }}
                              disabled={!isPaypalFormValid || isProcessing}
                            />
                            <Box sx={{ mt: 1.2 }}>
                              <PayPalButtons
                                fundingSource="card"
                                style={{ layout: 'vertical', shape: 'rect', width: '100%' }}
                                createOrder={async (data, actions) => {
                                  const orderId = await actions.order.create({
                                    purchase_units: [
                                      { amount: { value: amountUsd.toFixed(2) }, description: 'Self-Discovery Session' }
                                    ]
                                  });
                                  try { console.log('PayPal createOrder -> orderId:', orderId); } catch { }
                                  setPaypalOrderId(orderId);
                                  return orderId;
                                }}
                                onApprove={async (data, actions) => {
                                  setIsProcessing(true);
                                  try {
                                    await actions.order.capture();
                                    await handleFormSubmit({ preventDefault: () => {} }, data?.orderID || paypalOrderId);
                                    setForm({ firstName: '', lastName: '', email: '', phone: '' });
                                  } catch (e) {
                                    Swal.fire({ title: 'Payment Issue', text: 'We could not confirm your payment. Please try again or contact support.', icon: 'error' });
                                  } finally {
                                    setIsProcessing(false);
                                  }
                                }}
                                onError={(err) => {
                                  const details = err?.message ? ` (${err.message})` : '';
                                  Swal.fire({
                                    title: 'Card Payment Error',
                                    text: `PayPal could not complete the card flow. Please retry, or use PayPal login instead${details}.`,
                                    icon: 'error'
                                  });
                                }}
                                disabled={!isPaypalFormValid || isProcessing}
                              />
                            </Box>
                          </PayPalScriptProvider>
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
                <Box sx={{ flex: 1, border: paymentMethod === 'WHISH' ? '2px solid #1976d2' : '1px solid #ccc', borderRadius: 2, p: 2, bgcolor: paymentMethod === 'WHISH' ? '#e3f2fd' : '#fff', transition: 'all 0.2s' }}>
                  <Button
                    variant={paymentMethod === 'WHISH' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => setPaymentMethod('WHISH')}
                    fullWidth
                  >
                    Whish/OMT
                  </Button>
                  {paymentMethod === 'WHISH' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                      Pay via Whish/OMT transfer (instructions will be shown)
                    </Typography>
                  )}
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              {paymentMethod === 'WHISH' && (
                <Button variant="contained" color="primary" fullWidth onClick={handleWhish} disabled={isProcessing}>
                  Pay via Whish/OMT
                </Button>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper elevation={3} sx={{ p: 2, bgcolor: "custom.beige", maxWidth: 400, margin: '0 auto' }}>
              <Typography variant="h6" color="text.primary" gutterBottom>
                What’s Included
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Self-Discovery Journal (65 pages)" secondary="Guided workbook with reflection prompts, identity exercises, and values exploration." />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Self-Discovery Guide (158 pages)" secondary="A gentle, science-based guide that helps you reconnect with who you are, understand what truly matters to you, and move forward with clarity, intention, and self-compassion." />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="3 Psychological Tests" secondary="Strengths Assessment, Happiness Activities Diagnostic, and Life Satisfaction Scale." />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" color="text.primary" gutterBottom>
                How You’ll Receive It
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Email Delivery" secondary="We'll send the journal, guide and tests by email within 24 hours." />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Step 2: show info form after payment
  if (step === 2 && paymentMethod === 'WHISH') {
    // Only show info form for Whish, not PayPal
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, bgcolor: 'background.paper' }}>
              <Typography variant="h4" color="text.primary" gutterBottom>
                Enter Your Details
              </Typography>
              <form onSubmit={handleFormSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <input
                      name="firstName"
                      value={form.firstName}
                      onChange={handleFormChange}
                      placeholder="First Name"
                      required
                      style={{ width: '100%', padding: 12, marginBottom: 12, borderRadius: 4, border: '1.5px solid #888', fontSize: '1.15em', boxSizing: 'border-box' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <input
                      name="lastName"
                      value={form.lastName}
                      onChange={handleFormChange}
                      placeholder="Last Name"
                      required
                      style={{ width: '100%', padding: 12, marginBottom: 12, borderRadius: 4, border: '1.5px solid #888', fontSize: '1.15em', boxSizing: 'border-box' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleFormChange}
                      placeholder="Email Address"
                      required
                      style={{ width: '100%', padding: 12, marginBottom: 12, borderRadius: 4, border: '1.5px solid #888', fontSize: '1.15em', boxSizing: 'border-box' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleFormChange}
                      placeholder="Phone Number"
                      pattern="[0-9]+"
                      inputMode="numeric"
                      required
                      style={{ width: '100%', padding: 12, marginBottom: 12, borderRadius: 4, border: '1.5px solid #888', fontSize: '1.15em', boxSizing: 'border-box' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button type="submit" variant="contained" color="primary" fullWidth disabled={isProcessing} style={{ fontSize: '1.1em', padding: '12px 0' }}>
                      Complete Booking
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper elevation={3} sx={{ p: 3, bgcolor: "custom.beige" }}>
              <Typography variant="h6" color="text.primary" gutterBottom>
                What’s Included
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Self-Discovery Journal" secondary="Guided workbook with reflection prompts, identity exercises, and values exploration." />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleOutlineIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="3 Psychological Tests" secondary="Strengths Assessment, Happiness Activities Diagnostic, and Life Satisfaction Scale." />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" color="text.primary" gutterBottom>
                How You’ll Receive It
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Email Delivery" secondary="We’ll send the journal and tests by email within 24 hours." />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }
}
